import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock } from 'lucide-react';

interface SignInFormProps {
  onSwitchToSignUp: () => void;
  onForgotPassword: () => void;
}

export const SignInForm = ({ onSwitchToSignUp, onForgotPassword }: SignInFormProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const { signIn } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      toast({
        title: "Erreur",
        description: "Veuillez remplir tous les champs",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    
    try {
      const { error } = await signIn(email, password);

      if (error) {
        toast({
          title: "Erreur de connexion",
          description: "Email ou mot de passe incorrect",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Connexion réussie",
          description: "Bienvenue sur Getproof !"
        });
      }
    } catch (_error) {
      toast({
        title: "Erreur",
        description: "Une erreur inattendue s'est produite",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Logo */}
      <div className="flex items-center mb-8">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="hover:opacity-80 transition-opacity"
          aria-label="Retour à l'accueil"
        >
          <img 
            src="/lovable-uploads/c941c7a1-d271-4c8b-91a4-2611677da8a6.png" 
            alt="Getproof Logo" 
            className="h-10"
          />
        </button>
      </div>

      {/* Title */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Connectez-vous à votre compte
        </h1>
        <p className="text-gray-600">
          Entrez votre adresse e-mail et votre mot de passe pour vous connecter
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2 block">
            Email
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Entrez votre email"
              className="pl-10 h-12 border-gray-300 focus:border-teal-500 focus:ring-teal-500"
            />
          </div>
        </div>

        <div>
          <Label htmlFor="password" className="text-sm font-medium text-gray-700 mb-2 block">
            Mot de passe
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              id="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Entrez votre mot de passe"
              className="pl-10 pr-10 h-12 border-gray-300 focus:border-teal-500 focus:ring-teal-500"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="text-right">
          <button
            type="button"
            onClick={onForgotPassword}
            className="text-sm text-teal-600 hover:text-teal-700 hover:underline"
          >
            Mot de passe oublié ?
          </button>
        </div>

        <Button 
          type="submit" 
          className="w-full h-12 bg-teal-500 hover:bg-teal-600 text-white font-medium rounded-lg" 
          disabled={isLoading}
        >
          {isLoading ? 'Connexion...' : 'Se connecter'}
        </Button>

        <div className="text-center pt-4">
          <span className="text-sm text-gray-600">Vous n'avez pas de compte ? </span>
          <button
            type="button"
            onClick={onSwitchToSignUp}
            className="text-sm text-teal-600 hover:text-teal-700 hover:underline font-medium"
          >
            S'inscrire
          </button>
        </div>
      </form>

      {/* Footer links */}
      <div className="mt-12 pt-8 border-t border-gray-200">
        <div className="flex justify-center space-x-6 text-sm text-gray-500">
          <a href="#" className="hover:text-gray-700">Conditions générales</a>
          <a href="#" className="hover:text-gray-700">Politique de confidentialité</a>
        </div>
      </div>
    </div>
  );
};
